/**
 * @author       grady@zumper.com (Grady Kuhnline)
 * @copyright    Copyright (c) 2018, Zumper
 * @description  Creates a dependent selector that updates only if the args to resultFunc change
 */

import { memoizeResultsFunc } from './memoizeResultsFunc'
import { memoizeSelector } from './memoizeSelector'
import { Selector } from './types'

const mapSelectorsToArgs = (selectors: Selector[]) => (args: any[]) =>
  selectors.map((selector) => selector(...args))

const createDependentSelector = (selectors: Selector[]) => {
  const resultsFunc = memoizeResultsFunc(selectors[selectors.length - 1])
  selectors = selectors.slice(0, -1)
  const mapArgs = mapSelectorsToArgs(selectors)
  return memoizeSelector((...selectorArgs) => {
    const values = mapArgs(selectorArgs)
    return resultsFunc(...values)
  })
}

interface CreateSelector {
  <Args extends any[], Res1>(
    selector1: (...args: Args) => Res1
  ): (...args: Args) => Res1
  <Args extends any[], Res1, Res2>(
    selector1: (...args: Args) => Res1,
    selector2: (arg: Res1) => Res2
  ): (...args: Args) => Res2
  <Args extends any[], Res1, Res2, Res3>(
    selector1: (...args: Args) => Res1,
    selector2: (...args: Args) => Res2,
    selector3: (arg1: Res1, arg2: Res2) => Res3
  ): (...args: Args) => Res3
  <Args extends any[], Res1, Res2, Res3, Res4>(
    selector1: (...args: Args) => Res1,
    selector2: (...args: Args) => Res2,
    selector3: (...args: Args) => Res3,
    selector4: (arg1: Res1, arg2: Res2, arg3: Res3) => Res4
  ): (...args: Args) => Res4
  <Args extends any[], Res1, Res2, Res3, Res4, Res5>(
    selector1: (...args: Args) => Res1,
    selector2: (...args: Args) => Res2,
    selector3: (...args: Args) => Res3,
    selector4: (...args: Args) => Res4,
    selector5: (arg1: Res1, arg2: Res2, arg3: Res3, arg4: Res4) => Res5
  ): (...args: Args) => Res5
  <Args extends any[], Res1, Res2, Res3, Res4, Res5, Res6>(
    selector1: (...args: Args) => Res1,
    selector2: (...args: Args) => Res2,
    selector3: (...args: Args) => Res3,
    selector4: (...args: Args) => Res4,
    selector5: (...args: Args) => Res5,
    selector6: (
      arg1: Res1,
      arg2: Res2,
      arg3: Res3,
      arg4: Res4,
      arg5: Res5
    ) => Res6
  ): (...args: Args) => Res6
  <Args extends any[], Res1, Res2, Res3, Res4, Res5, Res6, Res7>(
    selector1: (...args: Args) => Res1,
    selector2: (...args: Args) => Res2,
    selector3: (...args: Args) => Res3,
    selector4: (...args: Args) => Res4,
    selector5: (...args: Args) => Res5,
    selector6: (...args: Args) => Res6,
    selector7: (
      arg1: Res1,
      arg2: Res2,
      arg3: Res3,
      arg4: Res4,
      arg5: Res5,
      arg6: Res6
    ) => Res7
  ): (...args: Args) => Res7
}

export const createSelector: CreateSelector = (...selectors: Selector[]) => {
  if (selectors.length > 1) {
    return createDependentSelector(selectors)
  }
  return selectors[0]
}
